/**
 * Central observable store. Holds the active route, the global language
 * toggle, and a tiny per-key cache for API responses so that switching
 * views does not refetch.
 *
 * Components never hold their own state — they subscribe via
 * `store.on("change", cb)`. The router mutates `store.state.route` and
 * calls `emit("change")`. View code is therefore re-rendered reactively
 * from a single source of truth, without any framework.
 */

export interface Route {
  name: RouteName;
  params: Record<string, string>;
  query: Record<string, string>;
}

export type RouteName =
  | "daily"
  | "dailyByDate"
  | "week"
  | "weeks"
  | "article"
  | "changelog"
  | "more"
  | "notFound";

export interface StoreState {
  route: Route;
  lang: "zh" | "en";
  cache: {
    daily?: import("./types").DailyDayGroup[];
    dailyByDate?: Record<string, import("./types").DailyListItem[]>;
    weeks?: import("./types").WeekSummary[];
    week?: Record<string, import("./types").Week>;
    article?: Record<string, import("./types").ArticleDetail>;
    changelog?: import("./types").ChangelogRow[];
  };
}

type Listener = (state: StoreState, prev: StoreState) => void;

let state: StoreState = {
  route: { name: "notFound", params: {}, query: {} },
  lang: "zh",
  cache: {},
};

const listeners = new Set<Listener>();

export function getStore(): Readonly<StoreState> {
  return state;
}

export function setRoute(next: Route): void {
  const prev = state;
  state = { ...state, route: next };
  emit(prev);
}

export function setLang(lang: "zh" | "en"): void {
  const prev = state;
  state = { ...state, lang };
  emit(prev);
}

export function getCache<K extends keyof StoreState["cache"]>(
  key: K,
): StoreState["cache"][K] | undefined {
  return state.cache[key];
}

export function setCacheEntry<K extends keyof StoreState["cache"]>(
  key: K,
  value: StoreState["cache"][K],
): void {
  const prev = state;
  state = {
    ...state,
    cache: { ...state.cache, [key]: value },
  };
  emit(prev);
}

export function setCacheMapEntry<K extends "dailyByDate" | "week" | "article">(
  key: K,
  id: string,
  value: NonNullable<StoreState["cache"][K]>[string],
): void {
  const prev = state;
  const slot = state.cache[key] as Record<string, unknown> | undefined;
  const nextSlot = { ...(slot ?? {}), [id]: value } as StoreState["cache"][K];
  state = {
    ...state,
    cache: { ...state.cache, [key]: nextSlot },
  };
  emit(prev);
}

export function on(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function emit(prev: StoreState): void {
  for (const fn of listeners) fn(state, prev);
}
